import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { EmploymentProfile } from '../types/employment.model'

/**
 * Employment data has nowhere to live on the backend yet: the `User` entity
 * holds only a Firebase `sub`, a PIN hash and a last-seen timestamp, and there
 * is no update endpoint for users at all. Until both exist, this is the store
 * of record, and it is the seam an API layer replaces, exactly as the schedule
 * does it.
 *
 * Scoped per restaurant: employment is a relationship with one venue, and the
 * same person may have a different rate in another.
 */

interface EmploymentState {
  profilesByUserId: Record<string, EmploymentProfile>
  setProfile: (userId: string, patch: Partial<EmploymentProfile>) => void
  removeProfile: (userId: string) => void
}

export const useEmploymentStore = create<EmploymentState>()(
  persist(
    set => ({
      profilesByUserId: {},

      setProfile: (userId, patch) =>
        set(state => ({
          profilesByUserId: {
            ...state.profilesByUserId,
            [userId]: {
              ...state.profilesByUserId[userId],
              ...patch,
              userId
            }
          }
        })),

      removeProfile: userId =>
        set(state => {
          const next = { ...state.profilesByUserId }
          delete next[userId]
          return { profilesByUserId: next }
        })
    }),
    {
      name: `employment-profiles-v1-${
        localStorage.getItem('restaurantId') ?? 'unknown'
      }`
    }
  )
)

/** Reads one profile without subscribing to the whole map. */
export const useEmploymentProfile = (
  userId: string | undefined
): EmploymentProfile | undefined =>
  useEmploymentStore(state =>
    userId ? state.profilesByUserId[userId] : undefined
  )
